import React from 'react';
import { FieldRenderProps } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { FormFieldWrapper } from '../FormFieldWrapper';
import { ComboBox, ComboBoxChangeEvent, ComboBoxFilterChangeEvent } from '@progress/kendo-react-dropdowns';
import { InputSuffix } from '@progress/kendo-react-inputs';
import { searchIcon } from '@progress/kendo-svg-icons';

interface FormComboBoxProps extends FieldRenderProps {
  isLoading?: boolean;
  onFilterChange?: (value: string) => void;
  onSelectionChange?: (value: unknown) => void;
  showSearchButton?: boolean;
}

interface ComboBoxSearchSuffixProps {
  disabled?: boolean;
  onSearch: () => void;
}

const ComboBoxSearchSuffix = ({ disabled, onSearch }: ComboBoxSearchSuffixProps): React.ReactElement => {
  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    onSearch();
  };

  return (
    <InputSuffix>
      <Button
        type="button"
        svgIcon={searchIcon}
        fillMode="flat"
        size="small"
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        className="cpsFieldActionButtonCompact"
        title="Search"
        aria-label="Search"
        disabled={disabled}
      />
    </InputSuffix>
  );
};

const getTypedInputValue = (container: HTMLDivElement | null): string => {
  const input = container?.querySelector('input');
  return input?.value?.trim() ?? '';
};

export const FormComboBox: React.FC<FormComboBoxProps> = fieldRenderProps => {
  const {
    defaultValue,
    value,
    choices,
    name,
    disabled,
    isLoading,
    onFilterChange,
    onSelectionChange,
    showSearchButton = false
  } = fieldRenderProps;
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const comboWrapperRef = React.useRef<HTMLDivElement>(null);
  const filterTextRef = React.useRef<string>('');
  const isAwaitingSearchResultsRef = React.useRef(false);
  const skipOpenFromFilterRef = React.useRef(false);

  React.useEffect(() => {
    if (!isAwaitingSearchResultsRef.current || disabled) {
      return;
    }

    if (!choices || choices.length === 0) {
      return;
    }

    isAwaitingSearchResultsRef.current = false;
    setIsOpen(true);
  }, [choices, disabled]);

  if (!name) {
    return <></>;
  }

  const handleFilterChange = (event: ComboBoxFilterChangeEvent): void => {
    const nextFilterValue = event.filter.value;
    if (!showSearchButton || nextFilterValue) {
      filterTextRef.current = nextFilterValue;
    }

    if (showSearchButton) {
      skipOpenFromFilterRef.current = true;
      setIsOpen(false);
      return;
    }

    isAwaitingSearchResultsRef.current = Boolean(nextFilterValue);
    setIsOpen(Boolean(nextFilterValue));

    if (onFilterChange) {
      onFilterChange(nextFilterValue);
    }
  };

  const handleChange = (event: ComboBoxChangeEvent): void => {
    isAwaitingSearchResultsRef.current = false;
    setIsOpen(false);

    if (onSelectionChange) {
      onSelectionChange(event.target.value);
    }
  };

  const handleOpen = (): void => {
    if (skipOpenFromFilterRef.current) {
      skipOpenFromFilterRef.current = false;
      return;
    }

    if (showSearchButton && (!choices || choices.length === 0)) {
      return;
    }

    setIsOpen(true);
  };

  const handleSearchClick = (): void => {
    if (!showSearchButton || !onFilterChange) {
      return;
    }

    const searchValue = (getTypedInputValue(comboWrapperRef.current) || filterTextRef.current).trim();
    if (!searchValue) {
      return;
    }

    isAwaitingSearchResultsRef.current = true;
    onFilterChange(searchValue);
  };

  return (
    <FormFieldWrapper
      validationMessage={fieldRenderProps.validationMessage}
      value={fieldRenderProps.value}
      touched={fieldRenderProps.touched}
      id={fieldRenderProps.id}
      valid={fieldRenderProps.valid}
      disabled={fieldRenderProps.disabled}
      hint={fieldRenderProps.hint}
      label={fieldRenderProps.label}
      wrapperStyle={fieldRenderProps.wrapperStyle}
      optional={fieldRenderProps.optional}
      modified={fieldRenderProps.modified}
      visited={fieldRenderProps.visited}
      onChange={fieldRenderProps.onChange}
      onFocus={fieldRenderProps.onFocus}
      onBlur={fieldRenderProps.onBlur}
      name={fieldRenderProps.name}
      isLoading={isLoading}
    >
      <div className="cpsComboBoxField" ref={comboWrapperRef}>
        <ComboBox
          data={choices || []}
          value={value || defaultValue || null}
          onChange={handleChange}
          onFilterChange={handleFilterChange}
          filterable={true}
          allowCustom={false}
          id={fieldRenderProps.name.toString()}
          validationMessage={fieldRenderProps.validationMessage || undefined}
          disabled={disabled}
          opened={isOpen}
          onOpen={handleOpen}
          onClose={() => setIsOpen(false)}
          suffix={
            showSearchButton
              ? () => <ComboBoxSearchSuffix disabled={disabled} onSearch={handleSearchClick} />
              : undefined
          }
        />
      </div>
    </FormFieldWrapper>
  );
};
